/**
 * ============================================================================
 * Firebase Configuration
 * 紫微斗數算命系統 - Firebase 配置與初始化
 * ============================================================================
 * 功能：
 * 1. Firebase App 初始化
 * 2. Firebase Authentication 設定
 * 3. App Check 保護
 * 4. 環境變數管理
 * ============================================================================
 */

import { initializeApp, FirebaseApp } from 'firebase/app';
import { getAuth, Auth } from 'firebase/auth';

/**
 * Firebase 配置物件
 * 從環境變數讀取，避免硬編碼敏感資訊
 */
const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
  measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID,
};

/**
 * 驗證 Firebase 配置是否完整
 */
const validateFirebaseConfig = (): boolean => {
  const requiredFields = [
    'apiKey',
    'authDomain',
    'projectId',
    'storageBucket',
    'messagingSenderId',
    'appId',
  ];

  const missingFields = requiredFields.filter(
    (field) => !firebaseConfig[field as keyof typeof firebaseConfig]
  );

  if (missingFields.length > 0) {
    console.error(
      '❌ Firebase 配置缺少必要欄位:',
      missingFields.join(', ')
    );
    console.error('請檢查 .env.development 檔案是否正確設定');
    return false;
  }

  return true;
};

/**
 * Firebase App 實例
 */
let app: FirebaseApp | null = null;

/**
 * Firebase Auth 實例
 */
let auth: Auth | null = null;

/**
 * 初始化 Firebase
 */
export const initializeFirebase = (): { app: FirebaseApp; auth: Auth } | null => {
  try {
    // 檢查是否已初始化
    if (app && auth) {
      return { app, auth };
    }

    // 驗證配置
    if (!validateFirebaseConfig()) {
      throw new Error('Firebase 配置無效');
    }

    // 初始化 Firebase App
    app = initializeApp(firebaseConfig);
    console.log('✅ Firebase App 初始化成功');
    console.log('   Project ID:', firebaseConfig.projectId);

    // 初始化 Firebase Auth
    auth = getAuth(app);
    console.log('✅ Firebase Auth 初始化成功');

    // 設定語言為繁體中文
    auth.languageCode = 'zh-TW';

    return { app, auth };
  } catch (error) {
    console.error('❌ Firebase 初始化失敗:', error);
    return null;
  }
};

/**
 * 取得 Firebase Auth 實例
 * 如果尚未初始化，則自動初始化
 */
export const getFirebaseAuth = (): Auth => {
  if (!auth) {
    const result = initializeFirebase();
    if (!result) {
      throw new Error('Firebase 初始化失敗');
    }
    auth = result.auth;
  }
  return auth;
};

/**
 * 取得 Firebase App 實例
 */
export const getFirebaseApp = (): FirebaseApp => {
  if (!app) {
    const result = initializeFirebase();
    if (!result) {
      throw new Error('Firebase 初始化失敗');
    }
    app = result.app;
  }
  return app;
};

/**
 * 檢查 Firebase 是否啟用
 *
 * 暫時禁用 Firebase Authentication（2026-01-23）
 * 原因：後端 Firebase 端點尚未實作，等待 Week 2-4 整合
 * TODO: 完成後端 /api/auth/firebase/verify 端點後重新啟用
 */
export const isFirebaseEnabled = (): boolean => {
  // 暫時強制返回 false，禁用 Firebase
  return false;

  // 原始邏輯（待後端完成後恢復）
  // const enabled =
  //   process.env.REACT_APP_ENABLE_FIREBASE_AUTH === 'true' &&
  //   validateFirebaseConfig();
  //
  // if (!enabled) {
  //   console.warn('⚠️ Firebase Authentication 未啟用或配置不完整');
  // }
  //
  // return enabled;
};

// 自動初始化（如果啟用）
// 暫時禁用（2026-01-23）- Firebase 整合未完成
// if (isFirebaseEnabled()) {
//   initializeFirebase();
// }

const firebaseApi = {
  initializeFirebase,
  getFirebaseAuth,
  getFirebaseApp,
  isFirebaseEnabled,
};

export default firebaseApi;
